import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';
import Modal from './Modal';
import Toast from './Toast';
import useFormCRUD from '../hooks/useFormCRUD';

// Canonical buckets — Phase 11.1 collapsed normalization to three. The
// system auto-converts every measurement UOM (gal/lb/dozen/etc.) to one
// of these three; the override is only used when the line UOM is a pack
// (case/box/bag) and the operator has to specify which bucket + how many.
// "+ Add new..." sentinel still allowed for exotic edge cases.
const BASE_UNIT_OPTIONS = ['fl_oz', 'oz', 'each'];
const BASE_UNIT_NEW_SENTINEL = '__add_new__';

/**
 * SKU catalog management with full-width table and modal-based Add/Edit forms.
 * Includes category suggestion capability for uncategorized items.
 *
 * @param {Object} props
 * @param {Function} [props.onUpdate] - Callback invoked after create/delete operations
 * @returns {JSX.Element} The SKU table with Add/Edit modals
 */
function SKUForm({ onUpdate }) {
  // Drives the base_unit dropdown's "+ Add new..." escape hatch. When true,
  // the form renders a typed-text input below the select. Initialized from
  // formData on edit (custom value → start in custom mode).
  const [baseUnitCustom, setBaseUnitCustom] = useState(false);
  // Sync custom-mode flag when modal opens for edit OR when formData arrives
  // post-edit-handler. If the persisted value isn't one of the canonical
  // options, render the form in custom mode so the user sees their value
  // in the typed-input box rather than in a select that doesn't list it.
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [undoData, setUndoData] = useState([]);
  const [showUndo, setShowUndo] = useState(false);
  const [skuSearch, setSkuSearch] = useState('');

  /**
   * Load vendors and categories for dropdowns.
   *
   * @returns {Promise<Object>} Object with vendors and categories arrays
   */
  const loadRefData = useCallback(async () => {
    const [vendorRes, catRes] = await Promise.all([
      api.getVendors(),
      api.getLevel3Categories()
    ]);
    return { vendors: vendorRes.data, categories: catRes.data };
  }, []);

  const {
    items: skus,
    formData,
    setFormData,
    editingId,
    showForm,
    setShowForm,
    submitting,
    toast,
    confirmModal,
    referenceData,
    setToast,
    loadItems: loadSKUs,
    handleSubmit,
    handleEdit,
    handleCancelEdit,
    handleDelete,
    handleCloseToast,
    handleCloseConfirmModal,
  } = useFormCRUD({
    apiGetAll: () => api.getSKUCatalog({}),
    apiCreate: api.createSKU,
    apiUpdate: api.updateSKU,
    apiDelete: api.deleteSKU,
    initialFormData: {
      vendor_id: '', sku: '', item_name: '', category_id: '',
      default_uom: '', notes: '',
      // Phase 11 Wave 7 (NORM-05): operator-curated normalization fields.
      // Strings in the form state; transformSubmitData below converts empty
      // strings to null and base_units_per_pack to a Number before submit.
      base_unit: '', base_units_per_pack: '',
    },
    entityName: 'SKU',
    useConfirmModal: true,
    getItemDisplayName: (item) => `${item.sku} - ${item.item_name}`,
    loadReferenceData: loadRefData,
    validateSubmitData: (data) => {
      const trimmedSku = data.sku?.trim();
      const trimmedName = data.item_name?.trim();
      if (!trimmedSku || !trimmedName) return 'SKU and Item Name cannot be blank';
      return null;
    },
    transformSubmitData: (data) => ({
      ...data,
      sku: data.sku.trim(),
      item_name: data.item_name.trim(),
      // Empty strings -> null so the backend stores SQL NULL (not '' or 0).
      base_unit: data.base_unit && data.base_unit.trim() !== '' ? data.base_unit.trim() : null,
      base_units_per_pack:
        data.base_units_per_pack === '' || data.base_units_per_pack == null
          ? null
          : Number(data.base_units_per_pack),
    }),
    onUpdate,
  });

  const vendors = referenceData.vendors || [];
  const categories = referenceData.categories || [];

  // When the form opens (or formData.base_unit changes from a closed → open
  // transition), decide whether to start the dropdown in select or custom
  // mode. A persisted value not in BASE_UNIT_OPTIONS renders in custom mode
  // so the user sees their existing value in the typed-input.
  useEffect(() => {
    if (showForm) {
      const v = formData.base_unit;
      setBaseUnitCustom(Boolean(v) && !BASE_UNIT_OPTIONS.includes(v));
    } else {
      setBaseUnitCustom(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, editingId]);

  const handleBaseUnitSelectChange = (val) => {
    if (val === BASE_UNIT_NEW_SENTINEL) {
      setBaseUnitCustom(true);
      setFormData({ ...formData, base_unit: '' });
    } else {
      setBaseUnitCustom(false);
      setFormData({ ...formData, base_unit: val });
    }
  };

  const filteredSKUs = skuSearch.trim()
    ? skus.filter(s =>
        s.sku.toLowerCase().includes(skuSearch.toLowerCase()) ||
        s.item_name.toLowerCase().includes(skuSearch.toLowerCase()) ||
        (s.vendor_name || '').toLowerCase().includes(skuSearch.toLowerCase())
      )
    : skus;

  /**
   * Fetch category suggestions for uncategorized SKUs from invoice history.
   * Shows a confirmation panel if suggestions are found, or an info message if none.
   */
  const handleSuggestCategories = async () => {
    setSuggesting(true);
    try {
      const response = await api.suggestSKUCategories();
      setSuggestions(response.data);
      if (response.data.length > 0) {
        const selected = {};
        response.data.forEach(s => { selected[s.sku_id] = true; });
        setSelectedSuggestions(selected);
        setShowSuggestions(true);
      } else {
        setToast({ message: 'No suggestions found. All SKUs already have categories or no matching invoice history exists.', type: 'info' });
      }
    } catch (error) {
      console.error('Error:', error);
      setToast({ message: 'Failed to get suggestions. Please try again.', type: 'error' });
    }
    setSuggesting(false);
  };

  /**
   * Toggle all suggestion checkboxes on or off.
   */
  const handleToggleAll = () => {
    const allSelected = suggestions.every(s => selectedSuggestions[s.sku_id]);
    const newSelection = {};
    suggestions.forEach(s => { newSelection[s.sku_id] = !allSelected; });
    setSelectedSuggestions(newSelection);
  };

  /**
   * Apply selected category suggestions via bulk update API, then reload SKUs.
   * Saves old category values for undo capability.
   */
  const handleApplySuggestions = async () => {
    const selected = suggestions.filter(s => selectedSuggestions[s.sku_id]);
    const updates = selected.map(s => ({ sku_id: s.sku_id, category_id: s.suggested_category_id }));
    if (updates.length === 0) return;

    const oldValues = selected.map(s => {
      const existing = skus.find(sk => sk.id === s.sku_id);
      return { sku_id: s.sku_id, category_id: existing?.category_id || null };
    });

    try {
      await api.bulkUpdateSKUCategories(updates);
      setShowSuggestions(false);
      setSuggestions([]);
      loadSKUs();

      setUndoData(oldValues);
      setShowUndo(true);
      setTimeout(() => setShowUndo(false), 10000);
    } catch (error) {
      setToast({ message: 'Error applying category suggestions', type: 'error' });
      console.error('Error:', error);
    }
  };

  /**
   * Undo the last bulk category apply by restoring old category_id values.
   */
  const handleUndoApply = async () => {
    try {
      await api.bulkUpdateSKUCategories(undoData);
      loadSKUs();
      setToast({ message: 'Categories reverted successfully', type: 'success' });
    } catch (error) {
      setToast({ message: 'Error undoing category changes', type: 'error' });
      console.error('Error undoing:', error);
    }
    setShowUndo(false);
    setUndoData([]);
  };

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={handleCloseToast} />}
      {confirmModal && (
        <ConfirmModal
          isOpen={true}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          variant={confirmModal.variant}
          onConfirm={confirmModal.onConfirm}
          onCancel={handleCloseConfirmModal}
        />
      )}

      {/* Edit SKU Modal */}
      <Modal
        isOpen={!!editingId}
        onClose={() => { handleCancelEdit(); setShowForm(false); }}
        title="Edit SKU"
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="edit-sku-vendor">Vendor <span className="req">*</span></label>
            <select
              id="edit-sku-vendor"
              className="input"
              value={formData.vendor_id}
              onChange={(e) => setFormData({...formData, vendor_id: e.target.value})}
              required
              aria-required="true"
            >
              <option value="">Select vendor...</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>

          <div className="field">
            <label className="label" htmlFor="edit-sku-code">SKU <span className="req">*</span></label>
            <input
              id="edit-sku-code"
              className="input"
              type="text"
              value={formData.sku}
              onChange={(e) => setFormData({...formData, sku: e.target.value})}
              required
              aria-required="true"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="edit-sku-item-name">Item Name <span className="req">*</span></label>
            <input
              id="edit-sku-item-name"
              className="input"
              type="text"
              value={formData.item_name}
              onChange={(e) => setFormData({...formData, item_name: e.target.value})}
              required
              aria-required="true"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="edit-sku-category">Category</label>
            <select
              id="edit-sku-category"
              className="input"
              value={formData.category_id}
              onChange={(e) => setFormData({...formData, category_id: e.target.value})}
            >
              <option value="">Select category...</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.full_path}</option>)}
            </select>
          </div>

          <div className="field">
            <label className="label" htmlFor="edit-sku-default-uom">Default UOM</label>
            <select
              id="edit-sku-default-uom"
              className="input"
              value={formData.default_uom}
              onChange={(e) => setFormData({...formData, default_uom: e.target.value})}
            >
              <option value="">-</option>
              <option value="each">Each</option>
              <option value="case">Case</option>
              <option value="box">Box</option>
            </select>
          </div>

          {/* Phase 11.1: normalization is auto-derived from each line item's
              UOM. This override is only needed when the invoice UOM is a
              pack ("case", "box", "bag") that doesn't tell us what's inside. */}
          <details className="settings-form__advanced">
            <summary>Pack-size override (advanced — most SKUs don't need this)</summary>
            <p className="hint" style={{ marginTop: 'var(--sp2)' }}>
              The system reads the unit-of-measure from each invoice (e.g.
              <strong> 5 LB</strong>, <strong>1 GAL</strong>, <strong>15 DZ</strong>)
              and converts to a per-unit price automatically.
            </p>
            <p className="hint">
              You only need to fill these fields in when the invoice
              unit-of-measure is a pack like <strong>case</strong>,
              <strong> box</strong>, or <strong>bag</strong> — those don't
              tell us what's inside, so we can't compute a per-unit price
              without your help.
            </p>
            <p className="hint">
              <strong>Example.</strong> This SKU ships as a "case" but each
              case contains 4 gallons:
            </p>
            <ul className="hint" style={{ marginTop: 0, paddingLeft: 'var(--sp4)' }}>
              <li>Base unit → <code>fl_oz</code></li>
              <li>Pack size → <code>512</code> (4 gal × 128 fl oz/gal)</li>
            </ul>
            <div className="field">
              <label className="label" htmlFor="edit-sku-base-unit">Base unit</label>
              <select
                id="edit-sku-base-unit"
                className="input"
                value={baseUnitCustom ? BASE_UNIT_NEW_SENTINEL : (formData.base_unit || '')}
                onChange={(e) => handleBaseUnitSelectChange(e.target.value)}
              >
                <option value="">(auto from invoice)</option>
                {BASE_UNIT_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                <option value={BASE_UNIT_NEW_SENTINEL}>+ Add new…</option>
              </select>
              {baseUnitCustom && (
                <input
                  id="edit-sku-base-unit-custom"
                  className="input"
                  type="text"
                  placeholder="New base unit (e.g. cup, slice)"
                  value={formData.base_unit || ''}
                  onChange={(e) => setFormData({...formData, base_unit: e.target.value})}
                  style={{ marginTop: 'var(--sp1)' }}
                  autoFocus
                />
              )}
            </div>

            <div className="field">
              <label className="label" htmlFor="edit-sku-base-units-per-pack">Pack size</label>
              <input
                id="edit-sku-base-units-per-pack"
                className="input sku-form__numeric"
                type="number"
                step="0.001"
                min="0.001"
                placeholder="e.g. 512 for a 4-gal case (in fl_oz), 72 for a 6-dozen crate (in eggs)"
                value={formData.base_units_per_pack === '' || formData.base_units_per_pack == null ? '' : formData.base_units_per_pack}
                onChange={(e) => setFormData({...formData, base_units_per_pack: e.target.value})}
                aria-describedby="edit-base-units-per-pack-hint"
              />
              <span className="hint" id="edit-base-units-per-pack-hint">
                Total number of <em>base units</em> in one purchase unit.
              </span>
            </div>
          </details>

          <div className="settings-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => { handleCancelEdit(); setShowForm(false); }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Add SKU Modal */}
      <Modal
        isOpen={showForm && !editingId}
        onClose={() => setShowForm(false)}
        title="Add SKU"
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          <div className="field">
            <label className="label" htmlFor="sku-vendor">Vendor <span className="req">*</span></label>
            <select
              id="sku-vendor"
              className="input"
              value={formData.vendor_id}
              onChange={(e) => setFormData({...formData, vendor_id: e.target.value})}
              required
              aria-required="true"
            >
              <option value="">Select vendor...</option>
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>

          <div className="field">
            <label className="label" htmlFor="sku-code">SKU <span className="req">*</span></label>
            <input
              id="sku-code"
              className="input"
              type="text"
              value={formData.sku}
              onChange={(e) => setFormData({...formData, sku: e.target.value})}
              required
              aria-required="true"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="sku-item-name">Item Name <span className="req">*</span></label>
            <input
              id="sku-item-name"
              className="input"
              type="text"
              value={formData.item_name}
              onChange={(e) => setFormData({...formData, item_name: e.target.value})}
              required
              aria-required="true"
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="sku-category">Category</label>
            <select
              id="sku-category"
              className="input"
              value={formData.category_id}
              onChange={(e) => setFormData({...formData, category_id: e.target.value})}
            >
              <option value="">Select category...</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.full_path}</option>)}
            </select>
          </div>

          <div className="field">
            <label className="label" htmlFor="sku-default-uom">Default UOM</label>
            <select
              id="sku-default-uom"
              className="input"
              value={formData.default_uom}
              onChange={(e) => setFormData({...formData, default_uom: e.target.value})}
            >
              <option value="">-</option>
              <option value="each">Each</option>
              <option value="case">Case</option>
              <option value="box">Box</option>
            </select>
          </div>

          {/* Phase 11.1: see Edit form for rationale. Pack-UOM overrides only. */}
          <details className="settings-form__advanced">
            <summary>Pack-size override (advanced — most SKUs don't need this)</summary>
            <p className="hint" style={{ marginTop: 'var(--sp2)' }}>
              The system reads the unit-of-measure from each invoice (e.g.
              <strong> 5 LB</strong>, <strong>1 GAL</strong>, <strong>15 DZ</strong>)
              and converts to a per-unit price automatically.
            </p>
            <p className="hint">
              You only need to fill these fields in when the invoice
              unit-of-measure is a pack like <strong>case</strong>,
              <strong> box</strong>, or <strong>bag</strong> — those don't
              tell us what's inside, so we can't compute a per-unit price
              without your help.
            </p>
            <p className="hint">
              <strong>Example.</strong> This SKU ships as a "case" but each
              case contains 4 gallons:
            </p>
            <ul className="hint" style={{ marginTop: 0, paddingLeft: 'var(--sp4)' }}>
              <li>Base unit → <code>fl_oz</code></li>
              <li>Pack size → <code>512</code> (4 gal × 128 fl oz/gal)</li>
            </ul>
            <div className="field">
              <label className="label" htmlFor="sku-base-unit">Base unit</label>
              <select
                id="sku-base-unit"
                className="input"
                value={baseUnitCustom ? BASE_UNIT_NEW_SENTINEL : (formData.base_unit || '')}
                onChange={(e) => handleBaseUnitSelectChange(e.target.value)}
              >
                <option value="">(auto from invoice)</option>
                {BASE_UNIT_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                <option value={BASE_UNIT_NEW_SENTINEL}>+ Add new…</option>
              </select>
              {baseUnitCustom && (
                <input
                  id="sku-base-unit-custom"
                  className="input"
                  type="text"
                  placeholder="New base unit (e.g. cup, slice)"
                  value={formData.base_unit || ''}
                  onChange={(e) => setFormData({...formData, base_unit: e.target.value})}
                  style={{ marginTop: 'var(--sp1)' }}
                  autoFocus
                />
              )}
            </div>

            <div className="field">
              <label className="label" htmlFor="sku-base-units-per-pack">Pack size</label>
              <input
                id="sku-base-units-per-pack"
                className="input sku-form__numeric"
                type="number"
                step="0.001"
                min="0.001"
                placeholder="e.g. 512 for a 4-gal case (in fl_oz), 72 for a 6-dozen crate (in eggs)"
                value={formData.base_units_per_pack === '' || formData.base_units_per_pack == null ? '' : formData.base_units_per_pack}
                onChange={(e) => setFormData({...formData, base_units_per_pack: e.target.value})}
                aria-describedby="sku-base-units-per-pack-hint"
              />
              <span className="hint" id="sku-base-units-per-pack-hint">
                Total number of <em>base units</em> in one purchase unit.
              </span>
            </div>
          </details>

          <div className="settings-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={submitting} style={{ flex: 1 }}>
              {submitting ? 'Saving...' : 'Add SKU'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Suggestions panel */}
      {showUndo && (
        <div data-testid="undo-toast" className="sku__undo-toast">
          <span>{undoData.length} {undoData.length === 1 ? 'category' : 'categories'} updated</span>
          <button type="button" onClick={handleUndoApply} className="sku__undo-btn">Undo</button>
        </div>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <div className="sku__suggestions-panel">
          <div className="sku__suggestions-header">
            <h3 className="sku__suggestions-title">Category Suggestions ({suggestions.length})</h3>
            <button type="button" onClick={handleToggleAll}
              aria-label={suggestions.every(s => selectedSuggestions[s.sku_id]) ? 'Deselect all suggestions' : 'Select all suggestions'}
              className="sku__toggle-all-btn">
              {suggestions.every(s => selectedSuggestions[s.sku_id]) ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <p className="sku__suggestions-desc">
            Based on invoice history. Uncheck items to exclude.
          </p>
          <div className="sku__suggestions-scroll">
            <table className="sku__suggestions-table">
              <thead><tr>
                <th scope="col"><span className="sr-only">Select</span></th>
                <th scope="col">SKU</th>
                <th scope="col">Item</th>
                <th scope="col">Suggested Category</th>
                <th scope="col">Confidence</th>
              </tr></thead>
              <tbody>
                {[...suggestions].sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || '')).map((s, idx, arr) => {
                  const prevVendor = idx > 0 ? arr[idx - 1].vendor_name : null;
                  const showHeader = s.vendor_name !== prevVendor;
                  return (
                    <React.Fragment key={s.sku_id}>
                      {showHeader && (
                        <tr>
                          <td colSpan="5" className="sku__vendor-group-header">
                            {s.vendor_name || 'Unknown Vendor'}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>
                          <input type="checkbox"
                            aria-label={'Select suggestion for ' + s.sku}
                            checked={!!selectedSuggestions[s.sku_id]}
                            onChange={e => setSelectedSuggestions(prev => ({ ...prev, [s.sku_id]: e.target.checked }))} />
                        </td>
                        <td>{s.sku}</td>
                        <td>{s.item_name}</td>
                        <td>{s.category_path}</td>
                        <td>{s.confidence} - {s.reason}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="sku__suggestions-actions">
            <button type="button" onClick={handleApplySuggestions}>
              Apply Selected ({Object.values(selectedSuggestions).filter(Boolean).length})
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowSuggestions(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* SKU table */}
      <div className="card">
        <div className="settings-form__table-header">
          <h3 className="settings-form__section-title" style={{ marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
            SKU Catalog ({skus.length})
          </h3>
          <div style={{ display: 'flex', gap: 'var(--sp2)', alignItems: 'center' }}>
            {skus.length > 3 && (
              <input
                className="input settings-form__search-input"
                type="text"
                placeholder="Search..."
                value={skuSearch}
                onChange={(e) => setSkuSearch(e.target.value)}
                aria-label="Search SKUs"
              />
            )}
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleSuggestCategories}
              disabled={suggesting}
            >
              {suggesting ? 'Analyzing...' : 'Suggest Categories'}
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Add SKU</button>
          </div>
        </div>

        {skus.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state__title">No SKUs yet</div>
            <div className="empty-state__description">Add your first SKU to get started.</div>
          </div>
        ) : (
          <div className="tbl-wrap tbl-wrap--md">
            <table>
              <thead>
                <tr>
                  <th scope="col">Vendor</th>
                  <th scope="col">SKU</th>
                  <th scope="col">Item Name</th>
                  <th scope="col">Category</th>
                  <th scope="col">UOM</th>
                  <th scope="col">Latest $/unit</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSKUs.map(s => (
                  <tr key={s.id} className={editingId === s.id ? 'table-row--editing' : undefined}>
                    <td>{s.vendor_name}</td>
                    <td><strong>{s.sku}</strong></td>
                    <td>{s.item_name}</td>
                    <td>
                      {/* Ruth UAT 2026-05-28 (GH #157): the row Edit button
                          DOES let you change category, but the affordance
                          wasn't visible — the category cell read as plain
                          text. Wrapping it in a button + amber chip styling
                          for "Uncategorized" makes the editability obvious
                          without adding another action button to the row. */}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => handleEdit(s)}
                        aria-label={`Change category for ${s.sku}`}
                        title="Click to change category"
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          textAlign: 'left',
                          cursor: 'pointer',
                          color: s.category_path ? 'inherit' : 'var(--amb700)',
                          textDecoration: 'underline dotted',
                        }}
                      >
                        <small>{s.category_path || 'Uncategorized — set category'}</small>
                      </button>
                    </td>
                    <td>{s.default_uom || <span className="settings-form__meta">&mdash;</span>}</td>
                    <td>
                      <span className="td-amt">
                        {s.latest_normalized_price == null
                          ? <span className="settings-form__meta">&mdash;</span>
                          : `$${Number(s.latest_normalized_price).toFixed(2)}/${s.latest_canonical_unit || 'unit'}`}
                      </span>
                    </td>
                    <td>
                      <div className="settings-form__action-btns">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleEdit(s)}
                          aria-label={'Edit ' + s.sku}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleDelete(s.id, s.sku)}
                          aria-label={'Delete ' + s.sku}
                          style={{ color: 'var(--error)' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

SKUForm.propTypes = {
  /** Callback invoked after create/delete operations */
  onUpdate: PropTypes.func,
};

export default SKUForm;
